import { ZONES_WITH_COLUMNS, GRID_ZONES_WITH_COLUMNS } from "~constants";
import { fetchModulesFromApi } from "~api/client/fetchModulesFromApi";
import {
    type ZoneRegion,
    type PublicationState,
    type CurationZone,
    type HydratedCurationModule,
    type AdModule,
    ZoneType,
    ModuleSource,
    ZoneColumnCombo,
} from "~types/types";
import getComponentName from "~utils/getComponentName";

export const zonesWithColumns: Record<ZoneType, ZoneRegion[]> = {
    switch: ["left-rail", "content", "right-rail"],
    righty: ["content", "right-rail"],
    lefty: ["left-rail", "content"],
    full: ["content"],
};

export const gridZonesWithColumns: Record<ZoneType, ZoneRegion[]> = {
    full: ["full-width-content-well"],
    righty: ["left-content-well", "grid-right-rail"],
    switch: ["unsupported"],
    lefty: ["unsupported"],
};

export function getModules(
    pageId: string,
    moduleIds: string,
    moduleVariations = "default",
    moduleTypes: string,
    locale: string,
    preview: boolean,
    publishedState: PublicationState
) {
    return fetchModulesFromApi({
        pageId,
        moduleIds,
        moduleTypes,
        moduleVariations,
        locale,
        preview,
        publishedState,
    });
}

export interface ModuleRenderingProps {
    componentName: string;
    renderingLocation: string;
    region?: string;
    zone?: string;
    isValidToTest?: boolean;
}

export const getRenderingData = (
    columnIndex: number,
    module: HydratedCurationModule | AdModule,
    zone: CurationZone,
    useGrid: boolean
): ModuleRenderingProps => {
    let renderingLocation: string = zone.kind;
    let region = "unsupported";

    if (!useGrid) {
        if (zone.kind === ZoneType.Righty) {
            renderingLocation =
                columnIndex === 0
                    ? ZoneColumnCombo.rightyContent
                    : ZoneColumnCombo.rightRail;
        } else if (zone.kind === ZoneType.Lefty) {
            renderingLocation =
                columnIndex === 0
                    ? ZoneColumnCombo.leftRail
                    : ZoneColumnCombo.leftyContent;
        } else if (zone.kind === ZoneType.Switch) {
            if (columnIndex === 0) renderingLocation = ZoneColumnCombo.leftRail;
            if (columnIndex === 1) {
                renderingLocation = ZoneColumnCombo.switchContent;
            }

            if (columnIndex === 2) {
                renderingLocation = ZoneColumnCombo.rightRail;
            }
        }

        region =
            (ZONES_WITH_COLUMNS[zone.kind] &&
                ZONES_WITH_COLUMNS[zone.kind][columnIndex]) ||
            "content";
    } else {
        if (zone.kind === ZoneType.Righty) {
            renderingLocation =
                columnIndex === 0
                    ? ZoneColumnCombo.leftContentWell
                    : ZoneColumnCombo.gridRightRail;
        } else if (zone.kind === ZoneType.Full) {
            renderingLocation = ZoneColumnCombo.fullWidthContentWell;
        } else {
            renderingLocation = ZoneColumnCombo.unsupported;
        }

        region =
            GRID_ZONES_WITH_COLUMNS[zone.kind] &&
            GRID_ZONES_WITH_COLUMNS[zone.kind][columnIndex];
    }

    const componentName = getComponentName(module.variation, module.type);

    return {
        componentName,
        renderingLocation,
        region,
        zone: zone.kind,
    };
};

export const setShadowModule = (
    currentModule: HydratedCurationModule,
    originalModule: HydratedCurationModule
): HydratedCurationModule => ({
    ...originalModule,
    componentName: getComponentName(
        originalModule.variation,
        originalModule.type
    ),
    duplicateOf: currentModule.duplicateOf,
    id: currentModule.id,
    renderingLocation: currentModule.renderingLocation,
    // Abba testing not available on shadow modules
    source: ModuleSource.Shadow,
    moduleIndex: currentModule.moduleIndex,
    zoneName: currentModule.zoneName,
});
